import {AGENCY_KEYS, useAgencyInviteMutation} from "@/api/agencyApi";
import {COMPANY_KEYS, useInviteCompanyMemberMutation} from "@/api/companyApi";
import UserAvatarPlaceholder from "@/assets/images/user_avatar_placeholder.svg";
import Modal from "@/components/Common/Modal";
import PermissionSelect from "@/components/MultiAccount/PermissionSelect";
import type {SubBrandInviteSerializer} from "@/generated/DjangoTypes";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import type {TPermission} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";

const RESEND = "RESEND";
const REMOVE = "REMOVE";

const UpdateInvitePermission: FC<{
  companyId: number;
  invite: SubBrandInviteSerializer;
  isAgency?: boolean;
  isLargeBrand?: boolean;
}> = ({companyId, invite, isAgency, isLargeBrand}) => {
  const {t} = useTranslation(["multi_account", "common"]);
  const permission = invite.permission!;
  const [showRemoveInvitationModal, setShowRemoveInvitationModal] = useState(false);

  const {mutate: inviteAgencyMember} = useAgencyInviteMutation({
    agencyId: companyId,
    email: invite.email,
    mutationOptions: {
      onSuccess: () => toast.success(t("resend_invitation_success")),
    },
  });

  const {mutate: inviteCompanyMember} = useInviteCompanyMemberMutation({
    companyId,
    email: invite.email,
    onSuccessCallback: sentPermission =>
      toast.success(t(sentPermission === permission ? "resend_invitation_success" : "permission_update_success")),
  });

  const {mutate: removeAgencyInvitation} = useMutation({
    mutationKey: AGENCY_KEYS.mAgencyMemberInviteRemoval(companyId),
    mutationFn: () => client.post(AGENCY_KEYS.mAgencyMemberInviteRemoval(companyId)[0], {email: invite.email}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgencyInvites(companyId)});
    },
  });

  const {mutate: removeCompanyInvitation} = useMutation({
    mutationKey: COMPANY_KEYS.mCompanyMemberInviteRemoval(companyId),
    mutationFn: () => client.post(COMPANY_KEYS.mCompanyMemberInviteRemoval(companyId)[0], {email: invite.email}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyInvites(companyId)});
    },
  });

  return (
    <>
      {showRemoveInvitationModal && (
        <Modal
          backgroundClassName="max-w-150"
          modalTitle={t("remove_invitation")}
          onClose={() => setShowRemoveInvitationModal(false)}
          onConfirm={() => (isAgency ? removeAgencyInvitation() : removeCompanyInvitation())}
          confirmButtonStyle="Danger"
          confirmButtonText={t("remove_invitation")}
          confirmButtonDataTestId="remove-invite-confirm-button">
          <div className="flex flex-col items-center gap-5">
            <span className="text-neutral-black text-center">
              {t(
                isAgency
                  ? isLargeBrand
                    ? "remove_invitation_confirmation_large_brand"
                    : "remove_invitation_confirmation_agency"
                  : "remove_invitation_confirmation_company",
              )}
            </span>
            <div className="border-neutral-grey-light-dark flex items-center gap-2 rounded-full border p-1 pr-2">
              <div className="size-6 shrink-0 rounded-full">
                <img src={UserAvatarPlaceholder} className="h-full w-full rounded-full object-fill" />
              </div>
              <span className="bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent">
                {invite?.email}
              </span>
            </div>
          </div>
        </Modal>
      )}
      <PermissionSelect
        dataTestId="invite-permission-select"
        value={permission}
        onChange={async value => {
          if (value === REMOVE) {
            setShowRemoveInvitationModal(true);
            return;
          }

          if (value === RESEND) {
            isAgency ? inviteAgencyMember() : inviteCompanyMember(permission);
            return;
          }

          if (value !== permission) {
            inviteCompanyMember(value as TPermission);
          }
        }}
        containerClassName="max-w-46.25"
        bottomOptions={[
          {
            label: t("resend_invitation"),
            value: RESEND,
          },
          {
            label: t("remove_invitation"),
            value: REMOVE,
          },
        ]}
        excludedPermissions={isAgency ? ["PURCHASE", "READ", "WRITE"] : []}
        topOptions={isAgency ? [{label: t("common:admin"), value: "ADMIN"}] : []}
      />
    </>
  );
};

export default UpdateInvitePermission;
